import { createSelector } from 'reselect';

import { normalizeWeightUnit } from '../../components/ProductInfo/helpers';
import { LISTING_STATUS } from '../products/constants/listingStatus';
import masterChannelOption from '../products/constants/masterChannelOption';
import {
  isVariantChannelDraft,
  isVariantChannelLive,
} from '../products/helpers/listingSyncHelpers';
import { getChannelEntryId, matchesChannelId } from '../products/helpers/channelHelpers';
import { findListingVariantEntity } from '../products/helpers/entityReadHelpers';
import resolveListingVariantLiveView from '../products/helpers/resolveListingVariantLiveView';
import { resolveChannelLabel } from '../products/helpers/showPublishedToChannelToast';
import {
  normalizeRatingValue,
  normalizeMediaValue,
  normalizeLinkValue,
  normalizeMeasurementValue,
  normalizeMoneyValue,
  normalizeReferenceValue,
  normalizeRichTextValue,
} from '../../components/ProductInfo/ProductMetafields/utils';
import { getKeywordNameFromPath } from '../helpers/keywords';
import localization from '../../shared/strings';
import { BUSY_CONTEXT } from '../products/helpers/busyContext';
import { selectProductsEntities } from './productEntitySelectors';
import {
  buildProductListingsMap,
  getDenormalizedVariantChannelEntry,
} from './productDenormalize';

const selectProducts = (state) => state.products;
const selectProductsUi = (state) => state.products.ui || {
  selectedChannelByProduct: {},
  activeTabByProductChannel: {},
  fetching: {
    listingAssets: {},
    listingVariantAssets: {},
    masterAssets: {},
    masterVariantAssets: {},
  },
};
const selectTeamSalesChannels = (state) => state.user?.team?.salesChannels || [];
const selectProductId = (_, productId) => productId;
const selectVariantId = (_, __, variantId) => variantId;

export const selectMasterProduct = createSelector(
  selectProducts,
  selectProductId,
  (products, productId) => products.items?.find(({ _id }) => _id === productId) || {},
);

const getProductVariant = (masterProduct, variantId) => (
  masterProduct.variants?.find(({ _id }) => _id === variantId) || null
);

const getLiveVariant = (liveVariants, variantId) => (
  liveVariants.find(({ masterVariantId }) => masterVariantId === variantId) || null
);

const getLiveVariants = (listing) => listing?.listingProduct?.live?.variants || [];

const getChannelOption = (channel, salesChannels = []) => {
  const channelId = getChannelEntryId(channel);
  const teamChannel = salesChannels.find((sc) => String(sc._id) === channelId);

  return {
    value: channelId,
    label: resolveChannelLabel(channelId, salesChannels),
    type: teamChannel?.type || channel.listingProduct?.salesChannel || channelId,
  };
};

const hasLiveVariant = (listing, variantId) => (
  getLiveVariants(listing).some(({ masterVariantId }) => masterVariantId === variantId)
);

const getVariantChannelStates = (entities, masterVariantId, productListings, variantId) => {
  const states = {};

  Object.values(entities?.listingVariants || {}).forEach((channelEntry) => {
    if (channelEntry?.masterVariantId !== masterVariantId) return;

    const channelId = getChannelEntryId(channelEntry);
    if (!channelId) return;

    states[channelId] = {
      hasDraft: isVariantChannelDraft(channelEntry),
      hasLive: isVariantChannelLive(channelEntry),
    };
  });

  Object.entries(productListings).forEach(([channelId, listing]) => {
    if (hasLiveVariant(listing, variantId)) {
      if (!states[channelId]) states[channelId] = { hasDraft: false, hasLive: false };
      states[channelId].hasLive = true;
    }
  });

  return states;
};

const getFirstListedChannelId = (productListings, salesChannelOptions) => (
  Object.entries(productListings).find(([, listing]) => listing?.listingStatus)?.[0]
  || salesChannelOptions[0]?.value
  || null
);

const getContextChannelId = (selectedChannelId, productListings, salesChannelOptions) => (
  selectedChannelId === 'master'
    ? getFirstListedChannelId(productListings, salesChannelOptions)
    : selectedChannelId
);

const getChannelOptionById = (channelOptions, channelId) => (
  channelId ? channelOptions.find(({ value }) => value === channelId) || null : null
);

const getAvailableActiveTab = ({
  activeTab,
  hasStaging,
  hasLive,
}) => {
  if (activeTab === LISTING_STATUS.DRAFT && hasStaging) return LISTING_STATUS.DRAFT;
  if (activeTab === LISTING_STATUS.PUBLISHED && hasLive) return LISTING_STATUS.PUBLISHED;
  return hasStaging ? LISTING_STATUS.DRAFT : LISTING_STATUS.PUBLISHED;
};

const getLayerState = ({
  listingStatus,
  hasLive,
  activeTab,
  forceMasterView = false,
}) => {
  const hasStaging = listingStatus === LISTING_STATUS.DRAFT;
  const normalizedHasLive = Boolean(hasLive);
  const isMasterView = forceMasterView || (!hasStaging && !normalizedHasLive);

  return {
    hasStaging,
    hasLive: normalizedHasLive,
    isMasterView,
    activeTab: isMasterView
      ? 'master'
      : getAvailableActiveTab({ activeTab, hasStaging, hasLive: normalizedHasLive }),
  };
};

const getListingState = (selectedListing, forceMasterView = false) => {
  const listingStatus = selectedListing?.listingStatus || null;
  return getLayerState({
    listingStatus,
    hasLive: Boolean(selectedListing?.listingProduct?.live),
    activeTab: selectedListing?.activeTab || listingStatus,
    forceMasterView,
  });
};

export const selectProductListings = createSelector(
  selectProductsEntities,
  selectProductsUi,
  selectProductId,
  (entities, ui, productId) => (
    buildProductListingsMap(entities, ui, productId)
  ),
);

export const selectChannelOptions = createSelector(
  selectProductListings,
  selectTeamSalesChannels,
  (productListings, salesChannels) => [
    masterChannelOption,
    ...Object.entries(productListings).map(([channelId, listing]) => ({
      value: channelId,
      label: resolveChannelLabel(channelId, salesChannels),
      type: listing?.listingProduct?.salesChannel || channelId,
    })),
  ],
);

export const selectSalesChannelOptions = createSelector(
  selectChannelOptions,
  (channelOptions) => channelOptions.filter(({ type }) => type !== 'master'),
);

export const selectChannelsSelectOptions = createSelector(
  selectChannelOptions,
  selectProductListings,
  (channelOptions, productListings) => {
    const masterOption = channelOptions.find(({ type }) => type === 'master') || null;
    const listedChannelOptions = channelOptions.filter(({ type, value }) => (
      type !== 'master' && Boolean(productListings[value])
    ));

    return [masterOption, ...listedChannelOptions].filter(Boolean);
  },
);

const selectDefaultSelectedChannelId = createSelector(
  selectProductListings,
  (productListings) => (
    Object.entries(productListings).find(([, listing]) => listing?.listingStatus === LISTING_STATUS.PUBLISHED)?.[0] || 'master'
  ),
);

export const selectSelectedProductChannelId = createSelector(
  selectProductsUi,
  selectProductId,
  selectChannelsSelectOptions,
  selectDefaultSelectedChannelId,
  selectTeamSalesChannels,
  (ui, productId, channelsSelectOptions, defaultSelectedChannelId, salesChannels) => {
    const selectedChannelId = ui.selectedChannelByProduct?.[productId];
    const isSelectedChannelValid = channelsSelectOptions.some(({ value }) => value === selectedChannelId);
    const isKnownSalesChannel = salesChannels.some((channel) => matchesChannelId(channel, selectedChannelId));

    if (selectedChannelId && (isSelectedChannelValid || isKnownSalesChannel)) {
      return selectedChannelId;
    }

    return defaultSelectedChannelId;
  },
);

export const selectSelectedChannelOption = createSelector(
  selectChannelsSelectOptions,
  selectSelectedProductChannelId,
  (channelsSelectOptions, selectedChannelId) => (
    channelsSelectOptions.find(({ value }) => value === selectedChannelId) || channelsSelectOptions[0] || null
  ),
);

export const selectProductChannelState = createSelector(
  selectChannelOptions,
  selectSelectedProductChannelId,
  selectSalesChannelOptions,
  selectProductListings,
  (
    channelOptions,
    selectedChannelId,
    salesChannelOptions,
    productListings,
  ) => {
    const contextChannelId = getContextChannelId(selectedChannelId, productListings, salesChannelOptions);
    const contextChannelOption = getChannelOptionById(channelOptions, contextChannelId);
    const selectedListing = contextChannelId ? (productListings[contextChannelId] || null) : null;
    const selectedListingActiveTab = selectedListing?.activeTab || selectedListing?.listingStatus || null;
    const {
      hasStaging,
      hasLive,
      isMasterView,
      activeTab,
    } = getListingState(selectedListing, selectedChannelId === 'master');

    return {
      contextChannelId,
      contextChannelOption,
      hasStaging,
      hasLive,
      isMasterView,
      activeTab,
      selectedListingActiveTab,
    };
  },
);

const selectSelectedProductListing = createSelector(
  selectProductListings,
  selectProductChannelState,
  (productListings, { contextChannelId }) => (contextChannelId ? productListings[contextChannelId] || null : null),
);

export const selectSelectedListingProduct = createSelector(
  selectSelectedProductListing,
  (selectedListing) => selectedListing?.listingProduct || {},
);

export const selectSelectedListingProductId = createSelector(
  selectSelectedListingProduct,
  (listingProduct) => listingProduct?._id ?? null,
);

export const selectSelectedListingGalleryAssets = createSelector(
  selectProductsEntities,
  selectSelectedListingProductId,
  (entities, listingProductId) => {
    if (!listingProductId) return [];

    return entities.assets?.listingAssets?.[listingProductId] || [];
  },
);

export const selectSelectedListingFetchingAssets = createSelector(
  selectProductsUi,
  selectSelectedListingProductId,
  (ui, listingProductId) => {
    if (!listingProductId) return false;

    return Boolean(ui.fetching?.listingAssets?.[listingProductId]);
  },
);

export const selectMasterProductGalleryAssets = createSelector(
  selectProductsEntities,
  selectProductId,
  (entities, productId) => entities.assets?.masterAssets?.[productId] || [],
);

export const selectMasterProductFetchingAssets = createSelector(
  selectProductsUi,
  selectProductId,
  (ui, productId) => Boolean(ui.fetching?.masterAssets?.[productId]),
);

export const selectSelectedLiveProduct = createSelector(
  selectSelectedProductListing,
  (selectedListing) => selectedListing?.listingProduct?.live || {},
);

export const selectSelectedLiveVariants = createSelector(
  selectMasterProduct,
  selectProductsEntities,
  selectProductChannelState,
  selectSelectedProductListing,
  (masterProduct, entities, { contextChannelId }, selectedListing) => {
    if (!contextChannelId) return [];

    const liveSnapshotsByMasterId = new Map(
      getLiveVariants(selectedListing).map((entry) => [entry.masterVariantId, entry]),
    );

    return (masterProduct.variants || [])
      .map((variant) => {
        const channelEntry = getDenormalizedVariantChannelEntry(
          entities,
          masterProduct,
          variant._id,
          contextChannelId,
        );
        const liveSnapshot = liveSnapshotsByMasterId.get(variant._id);

        if (channelEntry?.live) return channelEntry;
        if (liveSnapshot) {
          return channelEntry
            ? { ...channelEntry, live: channelEntry.live || liveSnapshot }
            : { ...liveSnapshot, masterVariantId: variant._id, live: liveSnapshot };
        }
        return null;
      })
      .filter(Boolean);
  },
);

export const selectLiveVariantForDisplay = createSelector(
  selectSelectedLiveVariants,
  selectVariantId,
  (liveVariants, variantId) => {
    if (!variantId) return null;
    const channelEntry = getLiveVariant(liveVariants, variantId);
    return resolveListingVariantLiveView(channelEntry);
  },
);

export const selectSelectedListingVariants = createSelector(
  selectMasterProduct,
  selectProductsEntities,
  selectProductChannelState,
  (masterProduct, entities, { contextChannelId }) => {
    if (!contextChannelId) return [];
    return (masterProduct.variants || [])
      .map((variant) => getDenormalizedVariantChannelEntry(
        entities,
        masterProduct,
        variant._id,
        contextChannelId,
      ))
      .filter(Boolean);
  },
);

export const selectDraftListingVariants = createSelector(
  selectMasterProduct,
  selectProductsEntities,
  selectProductChannelState,
  (masterProduct, entities, { contextChannelId }) => {
    if (!contextChannelId) return [];

    return (masterProduct.variants || [])
      .map((variant) => {
        const listingVariant = getDenormalizedVariantChannelEntry(
          entities,
          masterProduct,
          variant._id,
          contextChannelId,
        );
        if (!isVariantChannelDraft(listingVariant)) return null;

        return {
          _id: listingVariant._id,
          title: listingVariant.title || variant.title || variant._id,
        };
      })
      .filter(Boolean);
  },
);

export const selectSelectedListingVariantState = createSelector(
  selectMasterProduct,
  selectProductsEntities,
  selectProductChannelState,
  selectSelectedListingVariants,
  selectSelectedLiveVariants,
  selectSelectedProductListing,
  selectVariantId,
  (masterProduct, entities, channelState, listingVariants, liveVariants, selectedListing, variantId) => {
    const { contextChannelId } = channelState;
    const draftVariant = getDenormalizedVariantChannelEntry(
      entities,
      masterProduct,
      variantId,
      contextChannelId,
    );
    const liveVariant = getLiveVariant(liveVariants, variantId);
    const draftVariantsCount = listingVariants.length;
    const liveVariantsCount = liveVariants.length || listingVariants.filter((variant) => variant?.live).length;
    const hasVariantDraft = isVariantChannelDraft(draftVariant);
    const hasVariantLive = Boolean(
      isVariantChannelLive(draftVariant)
      || liveVariant
      || hasLiveVariant(selectedListing, variantId),
    );

    return {
      listingVariant: draftVariant || liveVariant,
      draftVariant,
      liveVariant,
      hasVariantDraft,
      hasVariantLive,
      isLastDraftVariant: hasVariantDraft && draftVariantsCount <= 1,
      isLastLiveVariant: hasVariantLive && liveVariantsCount <= 1,
    };
  },
);

export const selectIsVariantChannelPanelHydrating = createSelector(
  selectSelectedProductChannelId,
  selectProductChannelState,
  selectProductsEntities,
  selectMasterProduct,
  selectVariantId,
  (selectedChannelId, channelState, entities, masterProduct, variantId) => {
    if (!variantId || selectedChannelId === 'master') return false;

    const { contextChannelId } = channelState;
    if (!contextChannelId) return false;

    return !Object.values(entities?.listings || {}).some(
      (listing) => listing.masterProductId === masterProduct._id
        && matchesChannelId(listing, contextChannelId),
    );
  },
);

export const selectEffectiveChannelState = createSelector(
  selectProductChannelState,
  selectSelectedProductChannelId,
  selectSelectedListingVariantState,
  selectVariantId,
  (channelState, selectedChannelId, variantState, variantId) => {
    const onChannelTab = selectedChannelId && selectedChannelId !== 'master';
    if (!variantId || (channelState.isMasterView && !onChannelTab)) return channelState;

    const { hasVariantDraft, hasVariantLive } = variantState;
    const variantLayerState = getLayerState({
      listingStatus: hasVariantDraft ? LISTING_STATUS.DRAFT : null,
      hasLive: hasVariantLive,
      activeTab: channelState.selectedListingActiveTab || channelState.activeTab,
    });

    return {
      ...channelState,
      ...variantLayerState,
    };
  },
);

export const selectResolvedVariantForView = createSelector(
  selectMasterProduct,
  selectProductsEntities,
  selectProductChannelState,
  selectEffectiveChannelState,
  selectSelectedListingProduct,
  selectVariantId,
  (masterProduct, entities, channelState, effectiveChannelState, listingProduct, variantId) => {
    if (!variantId) return null;

    if (effectiveChannelState.isMasterView) {
      const masterVariant = getProductVariant(masterProduct, variantId);
      if (!masterVariant) return null;

      return {
        masterVariantId: variantId,
        linkVariantId: variantId,
        detachVariantId: variantId,
        variantType: 'masterVariant',
        listingId: null,
        layer: 'master',
      };
    }

    const { contextChannelId } = channelState;
    const channelEntry = getDenormalizedVariantChannelEntry(
      entities,
      masterProduct,
      variantId,
      contextChannelId,
    );

    if (!channelEntry?._id) return null;

    const layer = effectiveChannelState.activeTab === LISTING_STATUS.PUBLISHED ? 'live' : 'staging';

    return {
      masterVariantId: variantId,
      linkVariantId: channelEntry._id,
      detachVariantId: channelEntry._id,
      variantType: 'listingVariant',
      listingId: listingProduct?._id || channelEntry.baseProductId || null,
      layer,
    };
  },
);

export const selectVariantAssetsPool = createSelector(
  selectProductsEntities,
  selectProductsUi,
  (_, productId, resolvedVariant) => resolvedVariant,
  (entities, ui, resolvedVariant) => {
    if (!resolvedVariant) {
      return { pool: [], isFetching: false };
    }

    const { masterVariantId, linkVariantId, variantType } = resolvedVariant;

    if (variantType === 'masterVariant') {
      return {
        pool: entities.assets?.masterVariantAssets?.[masterVariantId] || [],
        isFetching: Boolean(ui.fetching?.masterVariantAssets?.[masterVariantId]),
      };
    }

    const listingVariantId = linkVariantId;

    return {
      pool: entities.assets?.listingVariantAssets?.[listingVariantId] || [],
      isFetching: Boolean(ui.fetching?.listingVariantAssets?.[listingVariantId]),
    };
  },
);

export const selectProductsForTree = createSelector(
  selectProducts,
  (productsState) => productsState.items.map(({
    _id, title, type, status, isBusy, variants, hasVariants, availableOptions,
  }) => ({
    _id,
    title,
    type,
    status,
    isBusy,
    variants,
    hasVariants,
    availableOptions,
  })),
);

export const selectProductIsBusy = (state, productId) => {
  const product = state.products.items.find(({ _id }) => _id === productId);
  return state.products.isBusy || Boolean(product?.isBusy);
};

export const selectProductBusyLoadingText = (state, productId) => {
  if (!selectProductIsBusy(state, productId)) {
    return null;
  }

  const { ACTIONS } = localization.DIALOGS.PRODUCT_DIALOGS.STATE_LAYERS;
  if (state.products.busyContext === BUSY_CONTEXT.FIELDS) {
    return ACTIONS.SAVING_CHANGES;
  }

  return ACTIONS.PROCESSING;
};

const selectVariantChannelStateMap = createSelector(
  selectProductsEntities,
  selectProductListings,
  selectMasterProduct,
  selectVariantId,
  (entities, productListings, masterProduct, variantId) => {
    if (!variantId) return null;
    const variant = getProductVariant(masterProduct, variantId);
    if (!variant) return null;
    return getVariantChannelStates(entities, variantId, productListings, variantId);
  },
);

export const selectVariantChannelNames = createSelector(
  selectVariantChannelStateMap,
  (variantChannelStates) => {
    if (!variantChannelStates) return null;
    return new Set(Object.keys(variantChannelStates));
  },
);

export const selectEffectiveIsMasterView = createSelector(
  selectEffectiveChannelState,
  ({ isMasterView }) => isMasterView,
);

export const selectVariantChannelStates = selectVariantChannelStateMap;

const MEDIA_TYPES_SET = new Set(['file', 'image', 'video']);
const MEASUREMENT_TYPES_SET = new Set(['weight', 'dimension', 'volume']);

const normalizeForDiff = (value, type) => {
  if (type === 'rating') return normalizeRatingValue(value);
  if (MEDIA_TYPES_SET.has(type)) return normalizeMediaValue(value);
  if (type === 'link') return normalizeLinkValue(value);
  if (MEASUREMENT_TYPES_SET.has(type)) return normalizeMeasurementValue(value);
  if (type === 'money') return normalizeMoneyValue(value);
  if (type === 'reference') return normalizeReferenceValue(value);
  if (type === 'richText') return normalizeRichTextValue(value);
  if (value === null || value === undefined || value === '') return null;
  if (Array.isArray(value) && value.length === 0) return null;
  return value;
};

const selectCustomFieldTypeMap = (state) => {
  const groups = state.productAttributes?.groups || [];
  const typeMap = {};
  groups.forEach((group) => {
    group.forEach((field) => {
      if (field.type && field.title) typeMap[field.title] = field.type;
    });
  });
  return typeMap;
};

const getItemCost = (item) => item?.cost ?? item?.unitCost?.amount ?? '';

const getKeywordDiffKey = ({ _id, title, path } = {}) => (
  _id || title || (path ? getKeywordNameFromPath(path) : '') || ''
);

const normalizeKeywordsForDiff = (keywords = []) => (
  [...keywords]
    .map(getKeywordDiffKey)
    .filter(Boolean)
    .sort()
);

const compareCustomFields = (listingFields, comparisonFields, typeMap = {}) => {
  if (!listingFields) return {};
  const diff = {};
  Object.keys(listingFields).forEach((key) => {
    const type = typeMap[key];
    const listingVal = normalizeForDiff(listingFields[key], type);
    const comparisonVal = normalizeForDiff((comparisonFields || {})[key], type);
    diff[key] = JSON.stringify(listingVal) !== JSON.stringify(comparisonVal);
  });
  return diff;
};

export const selectStagingDiff = createSelector(
  selectSelectedProductListing,
  selectMasterProduct,
  selectCustomFieldTypeMap,
  (selectedListing, masterProduct, customFieldTypeMap) => {
    const listingProduct = selectedListing?.listingProduct;
    if (!listingProduct) return {};
    const comparison = listingProduct.draftBaseline
      || listingProduct.live
      || masterProduct;
    const listingCategory = listingProduct.category || null;
    const comparisonCategory = comparison?.category || null;
    return {
      title: (listingProduct.title || '') !== (comparison?.title || ''),
      sku: (listingProduct.sku || '') !== (comparison?.sku || ''),
      vendor: (listingProduct.vendor || '') !== (comparison?.vendor || ''),
      productType: (listingProduct.productType || '') !== (comparison?.productType || ''),
      // TODO: need to discuss with product team
      category: JSON.stringify(listingCategory) !== JSON.stringify(comparisonCategory),
      descriptionHtml: (listingProduct.descriptionHtml || '') !== (comparison?.descriptionHtml || ''),
      handle: (listingProduct.handle || '') !== (comparison?.handle || ''),
      seoTitle: (listingProduct.seoTitle || '') !== (comparison?.seoTitle || ''),
      seoDescription: (listingProduct.seoDescription || '') !== (comparison?.seoDescription || ''),
      keywords: JSON.stringify(normalizeKeywordsForDiff(listingProduct.keywords))
        !== JSON.stringify(normalizeKeywordsForDiff(comparison?.keywords)),
      meta: compareCustomFields(
        listingProduct.meta,
        comparison?.meta,
        customFieldTypeMap,
      ),
      price: String(listingProduct.price ?? '') !== String(comparison?.price ?? ''),
      compareAtPrice: String(listingProduct.compareAtPrice ?? '') !== String(comparison?.compareAtPrice ?? ''),
      unitCost: String(getItemCost(listingProduct) ?? '') !== String(getItemCost(comparison) ?? ''),
      barcode: (listingProduct.barcode || '') !== (comparison?.barcode || ''),
      taxable: Boolean(listingProduct.taxable) !== Boolean(comparison?.taxable),
      tracked: Boolean(listingProduct.tracked) !== Boolean(comparison?.tracked),
      requiresShipping: Boolean(listingProduct.requiresShipping) !== Boolean(comparison?.requiresShipping),
      weight: String(listingProduct.weight?.value ?? '') !== String(comparison?.weight?.value ?? '') || normalizeWeightUnit(listingProduct.weight?.unit) !== normalizeWeightUnit(comparison?.weight?.unit),
      countryCodeOfOrigin: (listingProduct.countryCodeOfOrigin || '') !== (comparison?.countryCodeOfOrigin || ''),
      harmonizedSystemCode: (listingProduct.harmonizedSystemCode || '') !== (comparison?.harmonizedSystemCode || ''),
    };
  },
);

export const getChannelEntryListingProduct = (channelEntry) => channelEntry?.listingProduct ?? null;

export const getChannelEntryListingProductId = (channelEntry) => (
  getChannelEntryListingProduct(channelEntry)?._id ?? null
);

export const getChannelEntryHasListingProduct = (channelEntry) => (
  Boolean(getChannelEntryListingProduct(channelEntry))
);

export const getChannelEntryHasLive = (channelEntry) => (
  Boolean(getChannelEntryListingProduct(channelEntry)?.live)
);

export const getChannelEntryListingStatus = (channelEntry) => (
  getChannelEntryListingProduct(channelEntry)?.listingStatus
  ?? channelEntry?.listingStatus
  ?? null
);

export const getChannelEntryLiveUpdatedAt = (channelEntry) => (
  getChannelEntryListingProduct(channelEntry)?.live?.updatedAt ?? null
);

export const getChannelEntryListingUpdatedAt = (channelEntry) => (
  getChannelEntryListingProduct(channelEntry)?.updatedAt ?? null
);

export const getListingProductFromMap = (productListings, channelId) => (
  getChannelEntryListingProduct(productListings?.[channelId])
);

export const getListingProductIdFromMap = (productListings, channelId) => (
  getChannelEntryListingProductId(productListings?.[channelId])
);

export const hasListingProductInMap = (productListings, channelId) => (
  getChannelEntryHasListingProduct(productListings?.[channelId])
);

export const hasLiveInProductListingsMap = (productListings, channelId) => (
  getChannelEntryHasLive(productListings?.[channelId])
);

export const getListingProductStatusFromMap = (productListings, channelId) => (
  getChannelEntryListingStatus(productListings?.[channelId])
);

export const getListedChannelEntries = (masterProduct, productsState = null) => {
  if (productsState?.entities?.listings && masterProduct?._id) {
    const productListings = buildProductListingsMap(
      productsState.entities,
      productsState.ui || {},
      masterProduct._id,
    );
    return Object.values(productListings).filter((channel) => getChannelEntryListingProductId(channel));
  }

  return (masterProduct?.channels || []).filter((channel) => getChannelEntryListingProductId(channel));
};

export const productNeedsTargetsModal = (masterProduct, productsState = null) => {
  const hasVariants = (masterProduct?.variants || []).length > 0;
  const hasListings = getListedChannelEntries(masterProduct, productsState).length > 0;
  return hasVariants || hasListings;
};

export const buildListingToMasterMap = (masterProducts = [], productsState = null) => {
  if (productsState?.entities?.listings) {
    return Object.values(productsState.entities.listings).reduce((map, listing) => {
      if (listing?._id && listing?.masterProductId) {
        map[listing._id] = listing.masterProductId;
      }
      return map;
    }, {});
  }

  return masterProducts.reduce((map, master) => {
    getListedChannelEntries(master).forEach((channel) => {
      map[getChannelEntryListingProductId(channel)] = master._id;
    });
    return map;
  }, {});
};

export const getPublishedChannelIdsFromVariants = (variants = [], productsState = null) => {
  if (productsState?.entities?.listingVariants) {
    const masterVariantIds = new Set((variants || []).map(({ _id }) => _id));
    return [...new Set(
      Object.values(productsState.entities.listingVariants)
        .filter((listingVariant) => (
          masterVariantIds.has(listingVariant?.masterVariantId)
          && listingVariant?.listingStatus === LISTING_STATUS.PUBLISHED
        ))
        .map((listingVariant) => getChannelEntryId(listingVariant))
        .filter(Boolean),
    )];
  }

  return variants.flatMap((masterVariant) => (masterVariant.channels || [])
    .filter((channel) => channel.listingStatus === LISTING_STATUS.PUBLISHED)
    .map((channel) => getChannelEntryId(channel))
    .filter(Boolean));
};

export const getPublishedListingVariantIdsForChannel = (variants = [], channelId, productsState = null) => {
  if (productsState?.entities?.listingVariants) {
    const masterVariantIds = new Set((variants || []).map(({ _id }) => _id));
    return Object.values(productsState.entities.listingVariants)
      .filter((listingVariant) => (
        masterVariantIds.has(listingVariant?.masterVariantId)
        && matchesChannelId(listingVariant, channelId)
        && listingVariant?.listingStatus === LISTING_STATUS.PUBLISHED
      ))
      .map((listingVariant) => listingVariant._id)
      .filter(Boolean);
  }

  return variants.flatMap((masterVariant) => (masterVariant.channels || [])
    .filter((channel) => matchesChannelId(channel, channelId) && channel.listingStatus === LISTING_STATUS.PUBLISHED)
    .map((channel) => channel._id)
    .filter(Boolean));
};

export const getListingVariantsForChannel = (masterProduct, channelId, productsState = null) => {
  if (productsState?.entities?.listingVariants) {
    return (masterProduct?.variants || []).flatMap((masterVariant) => {
      const listingVariant = findListingVariantEntity(productsState, masterVariant._id, channelId);
      if (!listingVariant?._id) return [];
      return [{
        ...listingVariant,
        title: listingVariant.title || masterVariant.title,
        masterVariantId: masterVariant._id,
      }];
    });
  }

  return (masterProduct?.variants || []).flatMap((masterVariant) => {
    const listingVariant = (masterVariant.channels || []).find((entry) => matchesChannelId(entry, channelId));
    if (!listingVariant?._id) return [];
    return [{
      ...listingVariant,
      title: listingVariant.title || masterVariant.title,
      masterVariantId: masterVariant._id,
    }];
  });
};

export const buildListingVariantMap = (masterProduct, listingChannels, productsState = null) => (
  listingChannels.reduce((acc, channel) => {
    const channelId = getChannelEntryId(channel);
    if (!channelId) return acc;
    acc[channelId] = getListingVariantsForChannel(masterProduct, channelId, productsState);
    return acc;
  }, {})
);

export const getMasterVariantAssetsById = (masterProduct) => masterProduct?.variantAssetsById || {};

export const getMasterVariantAssets = (masterProduct, variantId) => (
  getMasterVariantAssetsById(masterProduct)[variantId] || []
);

export const getAllMasterVariantAssets = (masterProduct) => (
  Object.values(getMasterVariantAssetsById(masterProduct)).flat()
);

export const getProductGridItemTitle = (item) => (
  item?.title || item?.listingProduct?.title || ''
);

export const getProductGridItemListingThumbnail = (item) => (
  item?.listingProduct?.thumbnail?.small ?? null
);

export const selectVariantStagingDiff = createSelector(
  selectSelectedListingVariants,
  selectMasterProduct,
  selectSelectedProductListing,
  selectVariantId,
  selectCustomFieldTypeMap,
  (listingVariants, masterProduct, selectedListing, variantId, customFieldTypeMap) => {
    if (!variantId) return {};
    const draftVariant = listingVariants.find(({ masterVariantId }) => masterVariantId === variantId) ?? null;
    if (!draftVariant) return {};

    const liveVariants = getLiveVariants(selectedListing);
    const liveVariant = getLiveVariant(liveVariants, variantId);
    const masterVariant = (masterProduct.variants || []).find(({ _id }) => _id === variantId) || null;
    const comparison = draftVariant.draftBaseline
      || liveVariant
      || masterVariant;
    if (!comparison) return {};

    return {
      title: (draftVariant.title || '') !== (comparison.title || ''),
      description: (draftVariant.description || '') !== (comparison.description || ''),
      price: String(draftVariant.price ?? '') !== String(comparison.price ?? ''),
      compareAtPrice: String(draftVariant.compareAtPrice ?? '') !== String(comparison.compareAtPrice ?? ''),
      taxable: Boolean(draftVariant.taxable) !== Boolean(comparison.taxable),
      unitCost: String(getItemCost(draftVariant) ?? '') !== String(getItemCost(comparison) ?? ''),
      sku: (draftVariant.sku || '') !== (comparison.sku || ''),
      barcode: (draftVariant.barcode || '') !== (comparison.barcode || ''),
      tracked: Boolean(draftVariant.tracked) !== Boolean(comparison.tracked),
      inventoryPolicy: (draftVariant.inventoryPolicy || '') !== (comparison.inventoryPolicy || ''),
      availableForSale: Boolean(draftVariant.availableForSale) !== Boolean(comparison.availableForSale),
      taxCode: (draftVariant.taxCode || '') !== (comparison.taxCode || ''),
      countryCodeOfOrigin: (draftVariant.countryCodeOfOrigin || '') !== (comparison.countryCodeOfOrigin || ''),
      harmonizedSystemCode: (draftVariant.harmonizedSystemCode || '') !== (comparison.harmonizedSystemCode || ''),
      weight: String(draftVariant.weight?.value ?? '') !== String(comparison.weight?.value ?? '')
        || normalizeWeightUnit(draftVariant.weight?.unit) !== normalizeWeightUnit(comparison.weight?.unit),
      requiresShipping: Boolean(draftVariant.requiresShipping) !== Boolean(comparison.requiresShipping),
      meta: compareCustomFields(
        draftVariant.meta,
        comparison?.meta,
        customFieldTypeMap,
      ),
    };
  },
);
